/**
 * RTL pricing tab of the consolidated Admin → Pricing screen.
 *
 * Backend: `/admin/data/rtl-pricing/*` (app/routers/admin_data.py). This is the
 * central additive rate model for RTL/GUC:
 *
 *   requiredBaseRate = base_rate_12mo(product)
 *                    + term_adj + class_adj + loan-size uptick + max-leverage adj
 *   requiredCoupon   = requiredBaseRate + the correspondent's account-level spread
 *                      (what the borrower pays — the headline)
 *   buyRate          = requiredBaseRate (the investor's rate, BELOW the coupon)
 *   strip            = coupon − buyRate          (owner 2026-10-08)
 *
 * Five independently versioned sections, each saved on its own with a commit
 * note (`PUT /rtl-pricing/{section}` bumps just that section's version and keeps
 * the prior copy). The mixed scales are the thing to get right and are labelled
 * everywhere: BASE RATES are annual decimal fractions (0.0725 = 7.25%); every
 * ADJUSTMENT is a PERCENT stored verbatim (1.25 = 1.25%) — bps retired 2026-08-19.
 *
 * The unit rules are a contract, not a preference (frontend_documentation/
 * rtl-pricing-units-contract.md): adjustments go out EXACTLY as typed, never
 * scaled and never rounded, because `save_section` stamps `rateUnits: "percent"`
 * on whatever it receives — a bps payload is then read back verbatim and priced
 * 100x too high. The backend rejects any adjustment above 5% as a unit error;
 * that 422 is surfaced here as a pinned banner rather than a passing toast.
 *
 * The preview rail posts to `/rtl-pricing/preview`, which runs the same
 * `compute_rtl_rate` the sizer uses — against the SAVED config, so it shows what
 * the sizer quotes today rather than the effect of an unsaved edit.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  adminRtlPricingApi,
  RTL_PRICING_SECTIONS,
} from "../../../api/adminDataApi";
import { status } from "../../../theme/colors";
import PricingEditLog from "./PricingEditLog";
import RateMapSection from "./RateMapSection";
import TierListSection from "./TierListSection";
import RtlRatePreview from "./RtlRatePreview";
import RtlTierSpreadsPanel from "./RtlTierSpreadsPanel";
import {
  FormulaBanner,
  MetaChip,
  PricingPageLayout,
  PricingSection,
  SectionEmpty,
  T,
} from "./pricingUi";

export const RTL_RAIL = [
  { id: "rtl-base", label: "Base rates" },
  { id: "rtl-class", label: "Borrower class" },
  { id: "rtl-term", label: "Term adjustments" },
  { id: "rtl-lev", label: "Leverage" },
  { id: "rtl-upticks", label: "Loan-size upticks" },
  { id: "rtl-tiers", label: "Tier spreads" },
  { id: "rtl-log", label: "Edit log" },
];

const FORMULA = [
  { chip: "base rate (12 mo)" },
  { op: "+" },
  { chip: "term adj" },
  { op: "+" },
  { chip: "class adj" },
  { op: "+" },
  { chip: "loan-size uptick" },
  { op: "+" },
  { chip: "leverage adj" },
];

const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

const BANNER_TONE = {
  warn: { fg: T.warn, bg: T.warnSoft, line: T.warnLine },
  error: { fg: status.error.fg, bg: status.error.bg, line: "#fecaca" },
};

/**
 * A pane-level notice. Both things it carries — a units mismatch and a rejected
 * save — are too long and too consequential for a snackbar: the 422 names each
 * offending value, and the reader needs it still on screen while they fix the
 * cell it points at.
 */
const PaneBanner = ({ tone = "warn", title, children, onDismiss }) => {
  const c = BANNER_TONE[tone] || BANNER_TONE.warn;
  return (
    <Flx
      g={1.5}
      sx={{
        alignItems: "flex-start",
        bgcolor: c.bg,
        border: `1px solid ${c.line}`,
        borderRadius: "8px",
        px: 1.75,
        py: 1.25,
      }}
    >
      <Flx column g={0.375} sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: c.fg }}>{title}</Txt>
        <Txt sx={{ fontSize: 11.5, lineHeight: 1.55, color: T.ink2 }}>
          {children}
        </Txt>
      </Flx>
      {onDismiss ? (
        <IconButton size="small" onClick={onDismiss} aria-label="Dismiss">
          <CloseRounded sx={{ fontSize: 16 }} />
        </IconButton>
      ) : null}
    </Flx>
  );
};

PaneBanner.propTypes = {
  tone: PropTypes.oneOf(["warn", "error"]),
  title: PropTypes.node,
  children: PropTypes.node,
  onDismiss: PropTypes.func,
};

export default function RtlPricingPane() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [doc, setDoc] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // The last rejected save, kept on screen until dismissed or superseded.
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminRtlPricingApi.get(),
      adminRtlPricingApi.log().catch(() => []),
    ])
      .then(([d, l]) => {
        setDoc(d);
        setLog(l);
      })
      .catch((err) => {
        setDoc(null);
        enqueueSnackbar(errText(err, "Couldn't load RTL pricing config"), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => doc?.sections || {}, [doc]);
  const sectionData = useCallback((key) => sections?.[key]?.data, [sections]);
  const versionOf = useCallback(
    (key) => (sections?.[key]?.v ? `v${sections[key].v}` : "unseeded"),
    [sections],
  );

  // The section's last save, for its header. The section doc carries
  // updatedAt / updatedBy / note from save_section; the edit log is the
  // fallback (its entries are { at, by, section, note }, appended oldest first).
  const lastEditOf = useCallback(
    (key) => {
      const s = sections?.[key];
      if (s?.updatedAt) return { at: s.updatedAt, by: s.updatedBy, note: s.note };
      const entry = (log || [])
        .filter((e) => e?.section === key && e?.at)
        .reduce(
          (best, e) => (!best || String(e.at) > String(best.at) ? e : best),
          null,
        );
      return entry ? { at: entry.at, by: entry.by, note: entry.note } : null;
    },
    [sections, log],
  );

  const saveSection = useCallback(
    (section, data, note) => {
      setBusy(true);
      return adminRtlPricingApi
        .saveSection(section, data, note)
        .then((out) => {
          setSaveError(null);
          enqueueSnackbar(`${section} saved as v${out?.version}`, {
            variant: "success",
          });
          load();
        })
        .catch((err) => {
          const message = errText(err, "Save failed");
          // 422 is the unit guard. It names every offending value and explains
          // the scale in four sentences — unreadable in a toast that dismisses
          // itself, so it is pinned to the pane and the toast is made manual.
          if (err?.response?.status === 422) {
            setSaveError({ section, message });
            enqueueSnackbar(message, {
              variant: "error",
              persist: true,
              style: { whiteSpace: "pre-line", maxWidth: 520 },
              action: (key) => (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => closeSnackbar(key)}
                >
                  Dismiss
                </Button>
              ),
            });
          } else {
            enqueueSnackbar(message, { variant: "error" });
          }
          throw err;
        })
        .finally(() => setBusy(false));
    },
    [enqueueSnackbar, closeSnackbar, load],
  );

  const handleSeed = useCallback(() => {
    if (
      !window.confirm(
        "Seed every section with the code defaults? Each section gets a new version — existing values are replaced, not merged.",
      )
    )
      return;
    setBusy(true);
    adminRtlPricingApi
      .seed()
      .then(() => {
        enqueueSnackbar("Seeded from code defaults", { variant: "success" });
        load();
      })
      .catch((err) =>
        enqueueSnackbar(errText(err, "Seed failed"), { variant: "error" }),
      )
      .finally(() => setBusy(false));
  }, [enqueueSnackbar, load]);

  // The stored doc states its own scale. `save_section` stamps
  // rateUnits:"percent"; a doc WITHOUT the marker is read by the pricing engine
  // as legacy bps and divided by 100, while the tables below show the stored
  // numbers verbatim. That divergence is silent, so say it out loud. An unseeded
  // config prices off the code defaults and is not affected.
  const unitsUnmarked =
    !!doc && doc.seeded !== false && doc.rateUnits !== "percent";

  // Product keys come from the baseRates map — it's the taxonomy every other
  // per-product section is keyed by.
  const products = useMemo(
    () => Object.keys(sectionData("baseRates") || {}),
    [sectionData],
  );

  if (!doc) {
    return (
      <PricingPageLayout>
        <PricingSection>
          <SectionEmpty>
            {loading ? "Loading…" : "Couldn't load the RTL pricing config."}
          </SectionEmpty>
        </PricingSection>
      </PricingPageLayout>
    );
  }

  return (
    <PricingPageLayout
      nav={RTL_RAIL}
     
      aside={<RtlRatePreview products={products} />}
    >
      <FormulaBanner
        lead="Required base rate ="
        parts={FORMULA}
        tail="= buy rate → required coupon adds the correspondent's account spread (tier spread, or a per-product override, + overlay)"
      />

      {unitsUnmarked ? (
        <PaneBanner tone="warn" title="Stored units are unmarked">
          This config carries no rateUnits marker, so the pricing engine reads
          every adjustment as basis points and divides it by 100 — a 0.125%
          adjustment prices as 0.00125%. The tables below show the stored numbers
          verbatim, so the two disagree. Saving any section stamps the marker and
          resolves it.
        </PaneBanner>
      ) : null}

      {saveError ? (
        <PaneBanner
          tone="error"
          title={`${saveError.section} was not saved`}
          onDismiss={() => setSaveError(null)}
        >
          {saveError.message}
        </PaneBanner>
      ) : null}

      {/* Section version strip — every section versions independently, so the
          per-section chips below are the detail and this is the overview. */}
      <Flx ac jb g={1.5} wrap>
        <Flx ac g={0.75} wrap>
          {RTL_PRICING_SECTIONS.map((s) => (
            <MetaChip key={s} tone={sections?.[s]?.v ? undefined : "warn"}>
              {`${s} · ${versionOf(s)}`}
            </MetaChip>
          ))}
        </Flx>
        <Flx ac g={1}>
          {doc.seeded === false ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSeed}
              disabled={busy}
            >
              Seed defaults
            </Button>
          ) : null}
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Flx>
      </Flx>

      <RateMapSection
        key={`baseRates-${sections.baseRates?.v}`}
        id="rtl-base"
        title="Base rates by product"
        helper="One base per product at the 12-month term; longer terms price through the shared term adjustments below. Annual decimal fraction — 0.0725 renders as 7.250%."
        keyLabel="Product"
        valueLabel="Base rate"
        format="rate"
        version={versionOf("baseRates")}
        lastEdit={lastEditOf("baseRates")}
        data={sectionData("baseRates") || {}}
        busy={busy}
        onSave={(data, note) => saveSection("baseRates", data, note)}
      />

      <RateMapSection
        key={`classAdjustments-${sections.classAdjustments?.v}`}
        id="rtl-class"
        title="Borrower class adjustments"
        helper="Added to the base rate for the borrower class (A+ / A / B / C). Entered as a percent — 1.25 adds 1.25% to the base rate."
        keyLabel="Borrower class"
        valueLabel="Adjustment"
        format="percent"
        version={versionOf("classAdjustments")}
        lastEdit={lastEditOf("classAdjustments")}
        data={sectionData("classAdjustments") || {}}
        busy={busy}
        onSave={(data, note) => saveSection("classAdjustments", data, note)}
      />

      <RateMapSection
        key={`termAdjustments-${sections.termAdjustments?.v}`}
        id="rtl-term"
        title="Term adjustments"
        helper="Added to the base rate by loan term, keyed by term in months. Applies to every RTL product. Entered as a percent."
        keyLabel="Term (months)"
        valueLabel="Adjustment"
        format="percent"
        version={versionOf("termAdjustments")}
        lastEdit={lastEditOf("termAdjustments")}
        data={sectionData("termAdjustments") || {}}
        busy={busy}
        onSave={(data, note) => saveSection("termAdjustments", data, note)}
      />

      <TierListSection
        key={`maxLeverageAdjustments-${sections.maxLeverageAdjustments?.v}`}
        id="rtl-lev"
        title="Leverage adjustments"
        helper="A rate uptick when the sized leverage exceeds the band (LTV as a decimal fraction — 0.80 = 80%). Guideline-driven and distinct from LLPA price points; the LARGEST matching band wins."
        products={products}
        version={versionOf("maxLeverageAdjustments")}
        lastEdit={lastEditOf("maxLeverageAdjustments")}
        data={sectionData("maxLeverageAdjustments") || {}}
        columns={[
          { field: "maxLtv", header: "Max LTV", type: "fraction" },
          { field: "label", header: "Label", type: "text" },
          { field: "deltaPct", header: "Delta (%)", type: "percent" },
          { field: "active", header: "Active", type: "bool" },
        ]}
        newRow={() => ({ maxLtv: 0.75, label: "", deltaPct: 0, active: true })}
        busy={busy}
        onSave={(data, note) => saveSection("maxLeverageAdjustments", data, note)}
      />

      <TierListSection
        key={`loanAmountUpticks-${sections.loanAmountUpticks?.v}`}
        id="rtl-upticks"
        title="Loan-size upticks"
        helper="Half-open [min, max) loan-amount tiers per product; the first ACTIVE matching tier wins and loans outside every tier pick up no uptick. The delta is added to the base rate."
        products={products}
        version={versionOf("loanAmountUpticks")}
        lastEdit={lastEditOf("loanAmountUpticks")}
        data={sectionData("loanAmountUpticks") || {}}
        columns={[
          { field: "minLoan", header: "Min loan", type: "dollar" },
          { field: "maxLoan", header: "Max loan", type: "dollar" },
          { field: "label", header: "Label", type: "text" },
          { field: "deltaPct", header: "Delta (%)", type: "percent" },
          { field: "active", header: "Active", type: "bool" },
        ]}
        newRow={() => ({
          minLoan: 0,
          maxLoan: 0,
          label: "",
          deltaPct: 0,
          active: true,
        })}
        busy={busy}
        onSave={(data, note) => saveSection("loanAmountUpticks", data, note)}
      />

      {/* The account spread the buy rate adds on top of the required base rate.
          Its own collection (`/correspondents/admin/rtl_tier_spreads`), not a
          section of the model doc, so it versions per tier rather than here. */}
      <RtlTierSpreadsPanel />

      <PricingSection
        id="rtl-log"
        title="Edit log"
        desc="Every section save across the RTL model, newest first."
        dense
      >
        <PricingEditLog rows={log} empty="No edits logged yet." />
      </PricingSection>

      <Txt sx={{ fontSize: 11, color: T.faint, px: 0.5 }}>
        Every value on this tab is entered as a percent. Adjustments are stored
        exactly as typed — 0.125 is 0.125%, never rounded and never rescaled to
        basis points. Base rates are the one conversion: they store as decimal
        fractions (7.7700 % here is 0.0777 on the wire). An adjustment above 5%
        is rejected as a unit error rather than saved.
      </Txt>
    </PricingPageLayout>
  );
}

